import { memo, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { clsx } from 'clsx';
import { HStack } from '@library';
import Select from '../../../../core/components/input/Select/Select';
import useProductsListControls from '../../../hooks/useProductsListControls';
import useIsBicycleCategory from '../../../queries/useIsBicycleCategory';
import { useTireSideFiltersEnabled } from '../../TireSearchNew/ProductListTireSearch';
import styles from './ProductListControls.module.css';

interface SelectSortProps {
    sort: string;
    setSort: (sort: string) => void;
    small?: boolean;
}

interface SelectSizeProps {
    pageSize: number;
    setPageSize: (page: number) => void;
    small?: boolean;
}

export const SelectSort = memo(({ sort, setSort, small }: SelectSortProps) => {
    const intl = useIntl();
    const { enabled } = useTireSideFiltersEnabled();

    const { isBicycleCategory } = useIsBicycleCategory();

    const options = useMemo(() => {
        const sortOptions = [
            {
                label: intl.formatMessage({ id: 'price (descending)' }),
                value: 'retailPrice desc',
            },
            {
                label: intl.formatMessage({ id: 'price (ascending)' }),
                value: 'retailPrice asc',
            },
            {
                label: intl.formatMessage({ id: 'A to Z' }),
                value: 'name asc',
            },
            {
                label: intl.formatMessage({ id: 'Z to A' }),
                value: 'name desc',
            },
            {
                label: intl.formatMessage({ id: 'New to Old' }),
                value: 'new desc',
            },
        ];
        if (isBicycleCategory) {
            sortOptions.unshift({
                label: intl.formatMessage({ id: 'Relevance' }),
                value: 'custom asc',
            });
        }

        if (enabled) {
            sortOptions.unshift({
                label: intl.formatMessage({ id: 'ProductList Sort TireSet' }),
                value: 'tireCompatibility asc',
            });
        }
        return sortOptions;
    }, [enabled, isBicycleCategory, intl]);

    useEffect(() => {
        if (sort && !options.find(option => option.value === sort)) {
            setSort(options[0].value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sort, options]);

    return (
        <Select
            rightAligned
            menuSize="max"
            data-test-class="sortSelect"
            material={false}
            label={<FormattedMessage id="sort" />}
            small={small}
            noPadding={small}
            options={options}
            value={`${sort}`}
            onChange={setSort}
        />
    );
});

export const SelectSize = memo(
    ({ pageSize, setPageSize, small }: SelectSizeProps) => {
        return (
            <Select
                rightAligned
                menuSize="max"
                data-test-id="productListSizeSelect"
                material={false}
                label={<FormattedMessage id="number of products" />}
                options={[
                    { label: '24', value: '24' },
                    { label: '48', value: '48' },
                    { label: '72', value: '72' },
                    { label: '96', value: '96' },
                ]}
                small={small}
                noPadding={small}
                value={`${pageSize}`}
                onChange={async (size: string) =>
                    setPageSize(parseInt(size, 10))
                }
            />
        );
    }
);

export const ListControls = memo(({ small = false }: { small?: boolean }) => {
    const [pageSize, updatePageSize, sortField, updateSortField] =
        useProductsListControls();
    return (
        <div className={clsx({ [styles.listControls]: !small })}>
            <HStack justify="end" gap="gap32">
                <SelectSort
                    small={small}
                    sort={sortField}
                    setSort={updateSortField}
                />
                <SelectSize
                    small={small}
                    pageSize={pageSize}
                    setPageSize={updatePageSize}
                />
            </HStack>
        </div>
    );
});
